import _ from 'lodash';
import fecha from 'fecha';
import humanizeDuration from 'humanize-duration';

export const formatPrice = (amount, currency, asFree = false) => {
  if ((!amount && amount !== 0) || !currency) {
    return null;
  }

  if (asFree && amount === 0) {
    return 'FREE';
  }

  //
  // TODO - we support only USD, so we can render everything as en-US locale
  //
  // let locale = 'en-US';
  // if (typeof window === 'object') {
  //   if (navigator?.language) {
  //     const parts = navigator.language.split('-');
  //     if (parts.length > 0) {
  //       const country = parts.length > 1 ? parts[1] : parts[0];
  //       locale = `en-${country}`;
  //     }
  //   }
  // }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
};

/**
 * Short, human-friendly draft id — the last 6 chars of the id, prefixed with
 * `#`. Sequential draft ids double as version numbers, so the tail is the
 * distinguishing part. Single source of truth for the card's CopyableId and
 * the Versions dialog/toast.
 */
export const shortDraftId = (id) => `#${String(id).slice(-6)}`;

export const formatNumberOfPages = (numberOfPages = 0) => {
  if (numberOfPages > 1) {
    return `${numberOfPages} pages`;
  }
  if (numberOfPages === 1) {
    return '1 page';
  }
  return 'no pages';
};

export const formatDateTime = (dateString, dateFormat = 'YYYY-MM-DD HH:mm:ss Z') => {
  try {
    return fecha.format(fecha.parse(dateString, dateFormat), 'MMM Do, YYYY h:mm a');
  } catch (e) {
    return dateString;
  }
};

export const formatExifDateTime = dateString => {
  try {
    return fecha.format(fecha.parse(dateString, 'YYYY:MM:DD HH:mm:ss'), 'MMM Do, YYYY h:mm a');
  } catch (e) {
    return 'N/A';
  }
};

export const capitalizeString = string => {
  return string
    .split('(')
    .map(part =>
      part
        .split(' ')
        .map(word => {
          const value = _.lowerCase(word);
          if (['my canvas', 'mycanvas'].indexOf(value) >= 0) {
            return 'MyCanvas';
          }
          if (['family search', 'familysearch'].indexOf(value) >= 0) {
            return 'FamilySearch';
          }
          if (['a', 'an', 'of'].indexOf(value) >= 0) {
            return value;
          }
          return _.capitalize(word);
        })
        .join(' '),
    )
    .join('(');
};

export const formatDate = (dateObject, dateFormat = 'YYYY-MM-DD HH:mm:ss Z') => {
  return fecha.format(dateObject, dateFormat);
};

export const parseDate = (dateString, dateFormat = 'YYYY-MM-DD HH:mm:ss Z') => {
  return fecha.parse(dateString, dateFormat);
};

export const formatDuration = (
  startDate,
  endDate = new Date(),
  humanizeDurationOptions = { round: true, largest: 1 },
) => {
  return humanizeDuration(endDate - startDate, humanizeDurationOptions);
};

export const formatSeconds = (seconds) => {
  return humanizeDuration(seconds * 1000, { round: true, largest: 2 });
};

/**
 * Format an ISO timestamp as a short relative string for chat UIs.
 * Returns "just now", "Xm ago", "HH:MM" for today, or "MMM D" for older dates.
 */
export const formatRecentTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}min ago`;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (date >= today) {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }
  if (date.getFullYear() === now.getFullYear()) {
    return fecha.format(date, 'MMM D');
  }
  return fecha.format(date, 'MMM D, YYYY');
};

/**
 * Format a date string as a compact, human-friendly relative string.
 * Today returns "Just now" / "Xmin ago" / "Xh ago"; any other calendar day
 * drops the time and returns "MMM D" (this year) or "MMM D, YYYY" (older).
 */
export const formatCompactDateTime = (dateString, dateFormat = 'YYYY-MM-DD HH:mm:ss Z') => {
  if (!dateString) return null;
  try {
    const date = fecha.parse(dateString, dateFormat);
    if (!date) return dateString;
    const now = new Date();
    const isToday = date.getFullYear() === now.getFullYear()
      && date.getMonth() === now.getMonth()
      && date.getDate() === now.getDate();
    if (isToday) {
      const diffMin = Math.floor((now - date) / 60000);
      if (diffMin < 1) return 'Just now';
      if (diffMin < 60) return `${diffMin}min ago`;
      const diffHours = Math.floor(diffMin / 60);
      return `${diffHours}h ago`;
    }
    if (date.getFullYear() === now.getFullYear()) {
      return fecha.format(date, 'MMM D');
    }
    return fecha.format(date, 'MMM D, YYYY');
  } catch (e) {
    return dateString;
  }
};
